import React, { useState } from "react";
import { CircularProgress, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import style from "./index.module.css";

import defaultProfilePic from "@/assets/patient.png";

import GetImages from "@/components/GetImages";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchRTCTokenAgora,
  fetchRTMTokenAgora,
  setCallerDetails,
  setChannelName,
  setDrName,
  setIsVideoCallInitiated,
  setVideoAppointmentDetail,
  setVideoConsulationTitle,
  videoAppointmentDetails,
} from "@/store/slices/videoAppointment";

import { pushCallNotificationApi } from "@/utils/apis/doctor/appointment";
import { checkCameraOrMicPermission } from "@/utils";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

import { IAgoraTokenForVideoCallPermissionEnums } from "@/models/permission";
import { ICallType } from "@/models/ApiRequestResponse/doctor";
import { IModalKeyEnums } from "@/models/permission";

const IncomingCall: React.FC = () => {
  const [isVideoCallJoined, setIsVideoCallJoined] = useState(false);

  const dispatch = useAppDispatch();
  const { callerDetails } = useAppSelector(videoAppointmentDetails);

  const experience =
    callerDetails?.experience &&
    callerDetails?.experience.toString() + " yrs • ";
  const speciality =
    callerDetails?.speciality && callerDetails?.speciality.toString() + " • ";
  const qualification =
    callerDetails?.qualification &&
    JSON.parse(callerDetails?.qualification)[0]?.degree_name;

  const profileInfo = experience + speciality + qualification;
  const profileDetails =
    profileInfo?.toString()?.slice(0, 20) +
    (profileInfo?.toString()?.length > 20 ? "..." : "");

  const permissionRestrictionHandler = usePermissionRestriction();
  const navigate = useNavigate();

  const joinVideoCall = async () => {
    const permissionAvailable = await checkCameraOrMicPermission();
    if (!permissionAvailable) return;
    setIsVideoCallJoined(true);

    const isRTMPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.VIDEO_CALL,
      IAgoraTokenForVideoCallPermissionEnums.GENERATE_VIDEO_CALL_TOKEN_FOR_WEB
    );
    if (!isRTMPermissionGranted) return;
    await dispatch(fetchRTMTokenAgora(callerDetails?.bookingId));

    const isRTCTokenPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.VIDEO_CALL,
      IAgoraTokenForVideoCallPermissionEnums.GENERATE_VIDEO_CALL_TOKEN_FOR_APP
    );
    if (!isRTCTokenPermissionGranted) return;
    await dispatch(fetchRTCTokenAgora(callerDetails?.bookingId));
    dispatch(setChannelName(callerDetails?.bookingId));
    dispatch(
      setVideoAppointmentDetail({
        id: callerDetails?.bookingId,
        callerUserId: callerDetails?.callerId,
        patient: {
          memberId: callerDetails?.memberId ?? "",
        },
      })
    );
    dispatch(
      setVideoConsulationTitle({
        doctorName: callerDetails?.doctorName,
        patientName: callerDetails?.patientName,
        patientId: callerDetails?.patientId,
      })
    );

    dispatch(setCallerDetails(null));
    navigate("/join-room");
    dispatch(setIsVideoCallInitiated(true));
  };

  const rejectCall = () => {
    const isVideoCallNotificationPermissionGranted =
      permissionRestrictionHandler(
        IModalKeyEnums.VIDEO_CALL,
        IAgoraTokenForVideoCallPermissionEnums.SEND_VIDEO_CALL_NOTIFICATION
      );
    if (!isVideoCallNotificationPermissionGranted) return;

    pushCallNotificationApi({
      callingUserId: callerDetails?.callingUserId,
      callingUserName: callerDetails?.callerName,
      callerName: callerDetails?.callerName,
      bookingId: callerDetails?.bookingId,
      callerId: callerDetails?.callerId,
      memberId: callerDetails?.memberId,
      type: ICallType.REJECTED,
    });
    dispatch(setCallerDetails(null));
    dispatch(setDrName(""));
  };

  return (
    <Grid container className={style.callingScreen}>
      <Grid item display={"flex"} flexDirection={"column"} width={"100%"}>
        <Grid item className={style.profileInfo} width={"100%"}>
          <img
            src={callerDetails?.callerPhoto}
            alt="Profile"
            className={style.profilePic}
            onError={(e) => (e.currentTarget.src = defaultProfilePic)}
          />
          <Grid item className={style.doctorDetails}>
            <Grid
              item
              display={"flex"}
              justifyContent={{ xs: "center", md: "space-between" }}
              gap={1}
              width={"100%"}
            >
              <Typography
                component={"p"}
                className={style.profileName}
                textAlign={"center"}
              >
                {callerDetails?.callerName?.slice(0, 20) +
                  (callerDetails?.callerName?.length > 20 ? "..." : "")}
              </Typography>
              <Grid item className={style.ringingBox}>
                <GetImages name="ringingGreenIcon" height="14" width="14" />
                <Typography component={"p"} className={style.ringingText}>
                  Ringing
                </Typography>
              </Grid>
            </Grid>
            {(callerDetails?.experience ||
              callerDetails?.speciality ||
              callerDetails?.qualification) && (
              <Typography component={"p"} className={style.profileDetails}>
                {profileDetails}
              </Typography>
            )}
            <Grid item>
              <Typography component={"p"} className={style.statusText}>
                Incoming Video Call
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item className={style.callActionBtns}>
        <button className={style.endCallButton} onClick={rejectCall}>
          <GetImages name="VideoCallIcon" height="28" width="28" />
          <Typography component={"p"} className={style.buttonText}>
            Cancel
          </Typography>
        </button>
        {isVideoCallJoined ? (
          <Grid item className={style.pickCallButton}>
            <CircularProgress sx={{ color: "var(--neutral-100)" }} size={24} />
          </Grid>
        ) : (
          <button className={style.pickCallButton} onClick={joinVideoCall}>
            <GetImages name="VideoCallIcon" height="28" width="28" />
            <Typography component={"p"} className={style.buttonText}>
              {" "}
              Join Call
            </Typography>
          </button>
        )}
      </Grid>
    </Grid>
  );
};

export default IncomingCall;
